"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { workScopeStatusOptions } from "../../constants/options"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.installInteriorDoorsInfo"

function HomeInstallInteriorDoorsForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillInstallInteriorDoorsOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillInstallInteriorDoorsOn` })
    
    // Watch status field and claimed by field
    const installInteriorDoors = useWatch({ control, name: `${BASE_FIELD_NAME}.installInteriorDoors` })
    const claimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const goodwillInstallInteriorDoors = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillInstallInteriorDoors` })
    const goodwillInstallInteriorDoorsClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillInstallInteriorDoorsClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: installInteriorDoors,
        claimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillInstallInteriorDoors,
        claimedBy: goodwillInstallInteriorDoorsClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillInstallInteriorDoorsClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillInstallInteriorDoorsOn,
    })
    return (
        <HomeDetailsWrapperAccordion
            heading="Install Interior Doors"
            wrapperClassName="xl:grid-cols-6"
        >
            {/* Row 1: Install + Claimed By 50/50 (like Trim Install) */}
            <FormSelectInput
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Install Interior Doors" toggleName={`${BASE_FIELD_NAME}.goodwillInstallInteriorDoorsOn` as any} control={control} />,
                    wrapperClassName: "xl:col-span-3",
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.installInteriorDoors`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                    wrapperClassName: "xl:col-span-3",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillInstallInteriorDoorsOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Install Interior Doors Goodwill", labelStyle: goodwillLabelStyle, wrapperClassName: "xl:col-span-3" }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillInstallInteriorDoors`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Install Interior Doors Goodwill Claimed By", wrapperClassName: "xl:col-span-3" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillInstallInteriorDoorsClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row 2: All three "How many" questions */}
            <FormTextInput
                inputGroupProps={{
                    label: "How many interior doors need replacing?",
                    wrapperClassName: "xl:col-span-2",
                }}
                placeholder="How many interior doors need replacing?"
                name={`${BASE_FIELD_NAME}.doorsToReplace`}
                type="number"
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "How many interior doors need trim?",
                    wrapperClassName: "xl:col-span-2",
                }}
                placeholder="How many interior doors need trim?"
                name={`${BASE_FIELD_NAME}.doorsNeedTrim`}
                type="number"
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "How many linear feet of door casings?",
                    wrapperClassName: "xl:col-span-2",
                }}
                placeholder="How many linear feet of door casings?"
                name={`${BASE_FIELD_NAME}.linearFeetOfCasings`}
                type="number"
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Description",
                    wrapperClassName: "xl:col-span-6",
                }}
                rows={3}
                placeholder="Describe the interior door installation work needed"
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeInstallInteriorDoorsForm
